import type { FormField } from '../../../types/formBuilder';
import type { AgentTemplate, BillingPeriod, PublicAgentChatProps, UserAgentInstance } from '../../../types/agent';
import { getOnboardingAnswerKey } from '../../../utils/onboardingFormSchema';

export async function buildOnboardingAnswersForApi(
  answers: Record<string, unknown>,
  fields: FormField[]
): Promise<Record<string, string>> {
  const out: Record<string, string> = {};
  for (const field of fields) {
    if (field.hidden) continue;
    const key = getOnboardingAnswerKey(field);
    const v = answers[key];
    if (v === undefined || v === null) continue;

    if (typeof v === 'string') {
      out[key] = v;
      continue;
    }
    if (typeof v === 'number' && !Number.isNaN(v)) {
      out[key] = String(v);
      continue;
    }
    if (typeof v === 'boolean') {
      out[key] = v ? 'true' : 'false';
      continue;
    }
    if (v instanceof File) {
      try {
        const text = await v.text();
        out[key] = text ? `[${v.name}]\n${text}` : `[file:${v.name}]`;
      } catch {
        out[key] = `[file:${v.name}]`;
      }
      continue;
    }
    if (Array.isArray(v) && v.length && v[0] instanceof File) {
      const parts: string[] = [];
      for (const file of v as File[]) {
        try {
          const text = await file.text();
          parts.push(text ? `[${file.name}]\n${text}` : `[file:${file.name}]`);
        } catch {
          parts.push(`[file:${file.name}]`);
        }
      }
      out[key] = parts.join('\n---\n');
    }
  }
  return out;
}

export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsText(file);
  });
}

export function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error ?? new Error('Could not read image'));
    reader.readAsDataURL(file);
  });
}

/** Browsers often omit `file.type` on Windows; fall back to extension. */
export const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|bmp|svg|heic|avif)$/i;

export function isImageFile(file: File): boolean {
  const t = file.type || '';
  if (t.startsWith('image/')) return true;
  return IMAGE_EXT_RE.test(file.name);
}

export function extractAgentApiError(data: unknown): string {
  if (!data || typeof data !== 'object') return 'Upload failed';
  const err = (data as { error?: unknown }).error;
  if (typeof err === 'string') return err;
  if (
    err &&
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as { message: unknown }).message === 'string'
  ) {
    return (err as { message: string }).message;
  }
  return 'Upload failed';
}

export type UserMessageAttachment =
  | { kind: 'image'; url?: string; filename: string }
  | { kind: 'document'; filename: string };

export type ChatMessage =
  | { role: 'user'; content: string; attachments?: UserMessageAttachment[] }
  | { role: 'assistant'; content: string };

export function guessFilenameFromDataUrl(url: string): string {
  const m = url.match(/^data:image\/([^;]+);/i);
  if (!m) return 'image';
  const ext = m[1].toLowerCase() === 'jpeg' ? 'jpg' : m[1];
  return `image.${ext}`;
}

/** Rebuild body + attachments from DB (JSON parts, legacy `[Attached: …]` suffix, or plain text). */
export function parseStoredUserMessage(raw: string): { body: string; attachments: UserMessageAttachment[] } {
  const trimmed = raw.trim();
  if (trimmed.startsWith('{')) {
    try {
      const j = JSON.parse(raw) as {
        type?: string;
        parts?: Array<{ type?: string; text?: string; image_url?: { url?: string } }>;
      };
      if (j.type === 'parts' && Array.isArray(j.parts)) {
        const attachments: UserMessageAttachment[] = [];
        const textParts: string[] = [];
        for (const p of j.parts) {
          if (p.type === 'text' && typeof p.text === 'string') textParts.push(p.text);
          if (p.type === 'image_url' && p.image_url?.url) {
            attachments.push({
              kind: 'image',
              url: p.image_url.url,
              filename: guessFilenameFromDataUrl(p.image_url.url),
            });
          }
        }
        return { body: textParts.join('\n'), attachments };
      }
    } catch {
      /* fall through */
    }
  }
  const legacy = raw.match(/^([\s\S]*?)\n?\[Attached:\s*([^\]]+)\]\s*$/);
  if (legacy) {
    const names = legacy[2]
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const attachments: UserMessageAttachment[] = names.map((n) =>
      IMAGE_EXT_RE.test(n) ? { kind: 'image' as const, filename: n } : { kind: 'document' as const, filename: n }
    );
    return { body: legacy[1].trim(), attachments };
  }
  return { body: raw, attachments: [] };
}

const DEBUG_SESSION_KEY = 'hyper-debug-agent-454ccb';

export function debugAgentLog(
  location: string,
  message: string,
  hypothesisId: string,
  data: Record<string, unknown>
): void {
  if (!import.meta.env.DEV) return;
  const payload = {
    sessionId: '454ccb',
    location,
    message,
    data,
    timestamp: Date.now(),
    hypothesisId,
    runId: 'post-fix-picker-v2',
  };
  const line = JSON.stringify(payload);
  try {
    const prev = sessionStorage.getItem(DEBUG_SESSION_KEY) ?? '';
    sessionStorage.setItem(DEBUG_SESSION_KEY, `${prev}${line}\n`);
  } catch {
    /* quota / private mode */
  }
  try {
    type W = Window & { __HYPER_DEBUG_AGENT_454ccb__?: string[] };
    const w = window as W;
    w.__HYPER_DEBUG_AGENT_454ccb__ = w.__HYPER_DEBUG_AGENT_454ccb__ ?? [];
    w.__HYPER_DEBUG_AGENT_454ccb__.push(line);
  } catch {
    /* */
  }
  console.info('[454ccb]', line);
  const base = import.meta.env.BASE_URL || '/';
  const baseNorm = base.endsWith('/') ? base.slice(0, -1) : base;
  const ingestUrl = `${baseNorm}/ingest/bafa49c6-bd85-4555-b64f-2572eac683d7`;
  fetch(ingestUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '454ccb' },
    body: line,
  }).catch((err) => {
    console.warn('[454ccb] ingest fetch failed', ingestUrl, err);
  });
}

export function publicAgentDisplayTitle(
  template: AgentTemplate,
  pageConfig: PublicAgentChatProps['pageConfig']
) {
  const t = template.public_title?.trim();
  if (t) return t;
  return pageConfig.page_title?.trim() || template.name;
}

export function publicAgentDisplayDescription(
  template: AgentTemplate,
  pageConfig: PublicAgentChatProps['pageConfig']
) {
  const t = template.public_description?.trim();
  if (t) return t;
  return pageConfig.page_description?.trim() || '';
}

export function onboardingFieldValue(field: FormField, answers: Record<string, unknown>): unknown {
  const key = getOnboardingAnswerKey(field);
  if (answers[key] !== undefined) return answers[key];
  if (field.defaultValue !== undefined && field.defaultValue !== null) return field.defaultValue;
  if (field.type === 'boolean') return false;
  if (field.type === 'range') return field.min ?? 0;
  return '';
}

export function validateOnboardingRequired(fields: FormField[], answers: Record<string, unknown>): string | null {
  for (const field of fields) {
    if (field.hidden || !field.required) continue;
    if (field.type === 'prompt_builder') continue;
    const key = getOnboardingAnswerKey(field);
    const v = answers[key];
    if (field.type === 'boolean') {
      if (v !== true && v !== false) return field.label;
      continue;
    }
    if (field.type === 'file') {
      if (v == null || (Array.isArray(v) && v.length === 0)) return field.label;
      continue;
    }
    if (v === undefined || v === null) return field.label;
    if (typeof v === 'string' && !v.trim()) return field.label;
    if (typeof v === 'number' && Number.isNaN(v)) return field.label;
  }
  return null;
}

export function creditsForPeriod(pricing: AgentTemplate['pricing'], p: BillingPeriod): number {
  if (p === 'free') return 0;
  const v = pricing[p];
  if (typeof v === 'number' && v >= 0) return v;
  return -1;
}

export function periodEnabled(pricing: AgentTemplate['pricing'], p: BillingPeriod): boolean {
  if (p === 'free') return pricing.free === true;
  const v = pricing[p];
  return typeof v === 'number' && v >= 0;
}

export const BILLING_PERIOD_UI_ORDER: BillingPeriod[] = ['free', 'day', 'month', 'year'];

export function billingPeriodLabel(p: BillingPeriod): string {
  switch (p) {
    case 'free':
      return 'Free (Ad-supported)';
    case 'day':
      return 'One Day Pass';
    case 'month':
      return 'Monthly Pass';
    case 'year':
      return 'Yearly Pass';
    default:
      return p;
  }
}

export function billingPeriodBlurb(pricing: AgentTemplate['pricing'], p: BillingPeriod): string {
  if (p === 'free') return 'Ad-supported · no credits charged';
  if (creditsForPeriod(pricing, p) < 0) return '';
  return 'Ad-free, Higher Chat Limits and Faster Processing';
}

export function billingCreditBadgeText(pricing: AgentTemplate['pricing'], p: BillingPeriod): string {
  const n = creditsForPeriod(pricing, p);
  if (n < 0) return '—';
  return n.toLocaleString();
}

export function billingPeriodAccessAdjective(p: BillingPeriod): string {
  switch (p) {
    case 'day':
      return 'daily';
    case 'month':
      return 'monthly';
    case 'year':
      return 'yearly';
    default:
      return '';
  }
}

/** Paid access ended at `next_billing_at` (one-time period). Mirrors agent-api `isInstancePlanExpired`. */
export function isAgentPlanExpired(instance: UserAgentInstance): boolean {
  if (instance.selected_billing_period === 'free') return false;
  const cost = Number(instance.credits_per_period ?? 0);
  if (cost <= 0) return false;
  const next = instance.next_billing_at;
  if (!next) return false;
  return new Date() >= new Date(next);
}

/** Pixels from bottom to still count as “following” the transcript (auto-scroll + header sync). */
export const SCROLL_BOTTOM_THRESHOLD_PX = 88;

/** Single STT locale for Web Speech + Whisper; the model can reply in any language. */
export const DEFAULT_STT_LANG = 'en-US';
